// Geometry for the home page's iPad: the anchorage chart and the house battery
// line (TOM-980). Everything is computed from Gallivant RP's record in the
// chart's 520 x 590 viewBox (chartProjection.ts), at module load.
import { metresToChartUnits, projectToChart } from './chartProjection';
import { FT_PER_M } from './format';
import {
  GALLIVANT_ANCHOR,
  GALLIVANT_BOAT,
  GALLIVANT_EVENING,
  GALLIVANT_HOUSE_SOC,
  GALLIVANT_TRACK,
} from './gallivant';

type Point = { x: number; y: number };

const E = GALLIVANT_EVENING;
const r1 = (n: number) => Math.round(n * 10) / 10;

/** SVG path data through points, one decimal. */
export function pathThrough(points: readonly Point[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${r1(p.x)} ${r1(p.y)}`).join(' ');
}

/** A point `distance` units from `from` toward a compass bearing (0 is north, y grows south). */
export function towardBearing(from: Point, bearingDeg: number, distance: number): Point {
  const rad = (bearingDeg * Math.PI) / 180;
  return { x: from.x + distance * Math.sin(rad), y: from.y - distance * Math.cos(rad) };
}

export const CHART_ANCHOR = projectToChart(GALLIVANT_ANCHOR);
export const CHART_BOAT = projectToChart(GALLIVANT_BOAT);
export const CHART_HEADING_DEG = E.headingDeg;

/** The anchor watch's circle, in chart units. */
export const CHART_CIRCLE_R = metresToChartUnits(E.anchorRadiusM);

/** Her stored fixes from the anchor watch to 7:20 PM. */
export const CHART_TRACK_D = pathThrough(GALLIVANT_TRACK.map(projectToChart));

/** The circle's size label sits just outside the circle, up and to the right of the anchor. */
export const CHART_CIRCLE_LABEL: Point = {
  x: r1(CHART_ANCHOR.x + CHART_CIRCLE_R * 0.5),
  y: r1(CHART_ANCHOR.y - CHART_CIRCLE_R * 0.92),
};

/**
 * The wind arrow stands upwind of the boat and points the way the wind blows,
 * at the approved design's distance from her.
 */
export const CHART_WIND_ARROW_OFFSET = 104;
export const CHART_WIND_ARROW = {
  ...towardBearing(CHART_BOAT, E.windFromDeg, CHART_WIND_ARROW_OFFSET),
  rotateDeg: (E.windFromDeg + 180) % 360,
};

/** The scale bar: 200 ft in chart units. */
export const CHART_SCALE_FT = 200;
export const CHART_SCALE_LENGTH = metresToChartUnits(CHART_SCALE_FT / FT_PER_M);

/** The house battery card's line: a 260 x 60 box, 40% at the bottom, 100% at the top. */
export const SPARK_WIDTH = 260;
export const SPARK_HEIGHT = 60;
export const SPARK_MIN_PCT = 40;
export const SPARK_MAX_PCT = 100;

/** Path data for a series in the spark box; `close` returns the area under it. */
export function sparkPath(
  points: readonly (readonly [number, number])[],
  close = false,
  { width = SPARK_WIDTH, height = SPARK_HEIGHT, min = SPARK_MIN_PCT, max = SPARK_MAX_PCT } = {}
): string {
  const step = points.length > 1 ? width / (points.length - 1) : 0;
  const line = points
    .map(([, v], i) => {
      const y = height - ((Math.min(max, Math.max(min, v)) - min) / (max - min)) * height;
      return `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return close ? `${line} L${width} ${height} L0 ${height} Z` : line;
}

export const HOUSE_SPARK_LINE = sparkPath(GALLIVANT_HOUSE_SOC);
export const HOUSE_SPARK_AREA = sparkPath(GALLIVANT_HOUSE_SOC, true);
